import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { getUser, logout } from '../utils/auth';
import { ASSETS } from '../config/schools';
import ExamCountdown from '../components/ExamCountdown';
import { 
  FaTrophy, FaFire, FaStar, FaNewspaper, FaPencilAlt, FaMicrophone, 
  FaSearch, FaLightbulb, FaSignOutAlt, FaKeyboard, FaBook, FaChartLine, 
  FaUserTie, FaCreditCard, FaCalendarAlt, FaClock
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

function ConcoursDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const user = getUser();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
    fetchModules();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/concours/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await api.get('/concours/modules');
      setModules(response.data.modules || []);
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F4E3' }}>
        <div className="onomatopoeia">LOADING...</div>
      </div>
    );
  }

  const iconMap = {
    FaNewspaper, FaPencilAlt, FaMicrophone, FaSearch, FaLightbulb, 
    FaKeyboard, FaBook, FaChartLine, FaUserTie
  };

  const getModuleIcon = (iconName) => {
    return iconMap[iconName] || FaNewspaper;
  };

  return (
    <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
      {/* Header */}
      <header className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #EA580C 0%, #DC2626 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)'
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 py-4 relative z-10">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img src={ASSETS.DAILYGLOBE_LOGO} alt="DailyGlobe" className="w-10 h-10 object-contain" />
              <div>
                <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                  PRÉPA CONCOURS
                </h1>
                <p className="text-orange-100 text-xs">
                  Bienvenue, {dashboard?.student?.fullName || user?.fullName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Indicateur abonnement */}
              {dashboard?.subscription && (
                <div className="hidden md:flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                  <FaCreditCard className="text-white text-sm" />
                  <span className="text-white text-xs">
                    {dashboard.subscription.daysRemaining} jours restants
                  </span>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="comic-button bg-white/20 text-white border-2 border-white/50 text-sm px-3 py-1"
                data-testid="logout-button"
              >
                <FaSignOutAlt className="inline mr-1" /> Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Compte à rebours */}
        {dashboard?.examCountdown && (
          <div className="mb-8">
            <ExamCountdown 
              examDate={dashboard.examCountdown.examDate}
              targetSchools={dashboard.examCountdown.targetSchools}
            />
          </div>
        )}

        {/* Stats Banner */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          <div className="p-6 border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] bg-gradient-to-br from-yellow-400 to-orange-500 text-center" data-testid="score-panel">
            <FaTrophy className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="text-4xl font-bold text-white" style={{ fontFamily: 'Bangers, cursive' }}>
              {dashboard?.globalScore || 0}
            </h3>
            <p className="text-white text-lg" style={{ fontFamily: 'Bebas Neue' }}>Score Global</p>
          </div>
          
          <div className="p-6 border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] bg-gradient-to-br from-red-500 to-pink-600 text-center" data-testid="streak-panel">
            <FaFire className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="text-4xl font-bold text-white" style={{ fontFamily: 'Bangers, cursive' }}>
              {dashboard?.streak || 0}
            </h3>
            <p className="text-white text-lg" style={{ fontFamily: 'Bebas Neue' }}>Jours de suite</p>
          </div>
          
          <div className="p-6 border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] bg-gradient-to-br from-blue-500 to-cyan-600 text-center">
            <FaStar className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="text-4xl font-bold text-white" style={{ fontFamily: 'Bangers, cursive' }}>
              {dashboard?.recentAttempts?.length || 0}
            </h3>
            <p className="text-white text-lg" style={{ fontFamily: 'Bebas Neue' }}>Exercices réalisés</p>
          </div>
        </motion.div>

        {/* Message de motivation */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-orange-500 to-red-500 p-6 rounded-2xl border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] mb-8 text-white"
        >
          <div className="flex items-center gap-4">
            <div className="text-5xl">🎯</div>
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Bangers, cursive' }}>
                OBJECTIF CONCOURS !
              </h2>
              <p className="text-orange-100">
                {dashboard?.examCountdown?.days <= 30 
                  ? "La dernière ligne droite ! Entraîne-toi tous les jours pour maximiser tes chances."
                  : "Reste régulier dans ton entraînement. Chaque jour compte pour ta préparation !"
                }
              </p>
            </div>
          </div>
        </motion.div>

        {/* Modules disponibles */}
        <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Bangers, cursive', color: '#1B4965' }}>
          <FaBook className="inline mr-2" /> Tes modules d'entraînement
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map((module, idx) => {
            const IconComponent = getModuleIcon(module.icon);
            return (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                onClick={() => navigate(module.route)}
                className="comic-panel cursor-pointer hover:shadow-[8px_8px_0px_rgba(0,0,0,0.3)] transition-all transform hover:-translate-y-1"
                data-testid={`module-${module.id}`}
              >
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                  style={{ background: module.color }}
                >
                  <IconComponent className="text-3xl text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Bebas Neue', color: module.color }}>
                  {module.name}
                </h3>
                <p className="text-gray-600 text-sm">{module.description}</p>
                {module.requiresKeyboard && (
                  <span className="inline-block mt-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                    <FaKeyboard className="inline mr-1" /> Clavier requis
                  </span>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Activité récente */}
        {dashboard?.recentAttempts?.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8"
          >
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Bebas Neue', color: '#1B4965' }}>
              <FaClock className="inline mr-2" /> Activité récente
            </h2>
            <div className="comic-panel">
              <div className="space-y-3">
                {dashboard.recentAttempts.slice(0, 5).map((attempt, idx) => (
                  <div 
                    key={attempt.id || idx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        attempt.score >= 70 ? 'bg-green-100 text-green-600' : 
                        attempt.score >= 40 ? 'bg-yellow-100 text-yellow-600' : 
                        'bg-red-100 text-red-600'
                      }`}>
                        {attempt.score >= 70 ? '⭐' : attempt.score >= 40 ? '👍' : '💪'}
                      </div>
                      <div>
                        <p className="font-bold text-gray-800">{attempt.exerciseType}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(attempt.startedAt).toLocaleDateString('fr-FR', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg" style={{ color: attempt.score >= 70 ? '#10B981' : attempt.score >= 40 ? '#F59E0B' : '#EF4444' }}>
                        {attempt.score || 0}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Info abonnement */}
        {dashboard?.subscription && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8 p-6 border-2 border-dashed border-gray-300 rounded-xl text-center"
          >
            <p className="text-gray-500 text-sm">
              <FaCreditCard className="inline mr-1" />
              Abonnement actif jusqu'au {new Date(dashboard.subscription.endDate).toLocaleDateString('fr-FR', {
                day: 'numeric', month: 'long', year: 'numeric'
              })}
            </p>
            {dashboard.subscription.daysRemaining <= 7 && (
              <p className="text-orange-600 font-bold mt-2">
                ⚠️ Votre abonnement expire bientôt ! Pensez à le renouveler.
              </p>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default ConcoursDashboard;
